import { ValidationModel, ValidationResult, ValidationType } from "@/services/validation/types";
import { Vue } from "vue-property-decorator";
import i18n from "@/i18n";
import { checkIfUserExists } from "@/services/users/usersService";
import { checkIfExtensionExists } from "@/services/customExtensions/customExtensionService";

export async function validateValue(validator: ValidationModel): Promise<ValidationResult> {
    let isValid = (typeof validator.value !== "undefined" && validator.value !== null);
    const result = {} as ValidationResult;

    for (const rule of validator.rules) {
        if (rule.type === ValidationType.Required) {
            isValid = isValid &&
                validator.value.toString().length > 0;
            if (!isValid) {
                result.state = false;
                result.message = i18n.t("errors.required").toString();
                return new Promise(resolve => { resolve(result); });
            }
        }
        if (rule.type === ValidationType.Min) {
            const checkNumber = validator.rules.some(x => x.type === ValidationType.Number);
            isValid = checkNumber
                ? isValid && (+validator.value >= rule.value)
                : validator.value.length >= rule.value;

            if (!isValid) {
                result.state = false;
                result.message = checkNumber ? i18n.t("errors.min", { value: rule.value }).toString()
                    : i18n.t("errors.minString", { value: rule.value }).toString();
                return new Promise(resolve => { resolve(result); });
            }
        }
        if (rule.type === ValidationType.Max) {
            const checkNumber = validator.rules.some(x => x.type === ValidationType.Number);
            isValid = checkNumber
                ? isValid && (rule.value >= +validator.value)
                : validator.value.length >= rule.value;

            if (!isValid) {
                result.state = false;
                result.message = checkNumber
                    ? i18n.t("errors.max", { value: rule.value }).toString()
                    : i18n.t("errors.maxString", { value: rule.value }).toString();
                return new Promise(resolve => { resolve(result); });
            }
        }

        if (rule.type === ValidationType.Confirm) {
            isValid = isValid && rule.value === validator.value;
            if (!isValid) {
                result.state = false;
                result.message = i18n.t("errors.confirm").toString();
                return new Promise(resolve => { resolve(result); });
            }
        }

        if (rule.type === ValidationType.Number) {
            isValid = isValid && !isNaN(validator.value);
            if (!isValid) {
                result.state = false;
                result.message = i18n.t("errors.number").toString();
                return new Promise(resolve => { resolve(result); });
            }
        }

        if (rule.type === ValidationType.URL) {
            isValid = isValid && validUrl(validator.value);
            if (!isValid) {
                result.state = false;
                result.message = i18n.t("errors.url").toString();
                return new Promise(resolve => { resolve(result); });
            }
        }
        if (rule.type === ValidationType.NonNegativeNumber) {
            isValid = isValid && (!isNaN(validator.value) && +validator.value > 0);
            if (!isValid) {
                result.state = false;
                result.message = i18n.t("errors.nonNegativeNumber").toString();
                return new Promise(resolve => { resolve(result); });
            }
        }

        if (rule.type === ValidationType.UniqueEmail) {
            isValid = isValid && !(await checkIfUserExists(validator.value));
            if (!isValid) {
                result.state = false;
                result.message = i18n.t("errors.uniqueEmail").toString();
                return new Promise(resolve => { resolve(result); });
            }
        }

        if (rule.type === ValidationType.UniqueExtensionName) {
            isValid = isValid && !(await checkIfExtensionExists(validator.value, rule.entityId));
            if (!isValid) {
                result.state = false;
                result.message = i18n.t("errors.uniqueExtensionName").toString();
                return new Promise(resolve => { resolve(result); });
            }
        }
    }

    result.state = isValid;
    return new Promise(resolve => { resolve(result); });
}

export async function validateForm(validators: any[]): Promise<boolean> {
    await validateUntouchedFields(validators);
    return new Promise(resolve => resolve(isFormValid(validators)));
}

async function validateUntouchedFields(validators: any[]) {
    for (const validator of validators) {
        for (const index in validator) {
            const item = validator[index];
            if (item.state || !item.active) { continue; }
            const response = await validateValue(item);
            Vue.set(item, "message", response.message);
            Vue.set(item, "state", response.state);
        }
    }
}

function isFormValid(validators: any[]) {
    for (const validator of validators) {
        if (Object.values(validator)
            .some((x: any) => typeof x.state !== "undefined" && x.state === false)) {
            return false;
        };
    }
    return true;
}

function validUrl(str: string) {
    const urlPattern = new RegExp("^(https|http|ftp):\\/\\/"); // protocol
    const uncPattern = new RegExp("^(\\\\\\\\[^/\\\\\\]\\[\":;|<>+=,?* _]+\\\\[^/\\\\\\]\\[\":;|<>+=,?*]+)((?:\\\\[^\\\\/:*?\"<>|]+)*\\\\?)$");
    return !!urlPattern.test(str) || !!uncPattern.test(str);
}
